export interface ExpressCheckoutAddress {
  city: string;
  recipient: {
    firstName: string;
    gender: string;
    lastName: string;
    type: string;
  };
  countryCode: string;
  createdAt: string;
  updatedAt: string;
  id: number;
  isDefault: {
    billing: boolean;
    shipping: boolean;
  };
  customer: {id: number};
  street: string;
  houseNumber: string;
  zipCode: string;
  isParcelShop?: boolean;
  hash?: string;
  additional?: string;
}

export const Payment = {
  PAYPAL: 'paypal_instant',
  RATE_PAY: 'ratepay_invoice',
} as const;

export type AllowedPaymentTypes =
  | typeof Payment.RATE_PAY
  | typeof Payment.PAYPAL;

export interface PaymentConfiguration {
  configuration: {
    deviceFingerPrinting?: {
      deviceIdentId: string;
      deviceIdentLocation: string;
      deviceIdentToken: string;
    };
    expressSettings?: {
      clientId: string;
      commit: boolean;
      intent: string;
    };
    mustAcceptTermsAndConditions: boolean;
  };
  isLastUsedPaymentMethod: boolean;
  key: AllowedPaymentTypes;
}
export interface ExpressCheckoutAuthResponse {
  addresses?: {
    billing: ExpressCheckoutAddress;
    shipping: ExpressCheckoutAddress;
  };
  currency: string;
  authorizationId: string;
  basketId: string;
  customerId: number;
  payments?: Array<PaymentConfiguration>;
  platformAppId?: number;
}

export type ExpressCheckoutUserData = Omit<
  ExpressCheckoutAuthResponse,
  'payments'
> & {
  payment: PaymentConfiguration;
};

export interface OrderConfirmation {
  authorizationId: string;
  basketId: string;
  confirmedOn: string;
  customerId: number;
  orderId: number;
  orderKey: string;
  orderSuccessParameters: {
    cbd: string;
    pmm: string;
  };
  platformAppId?: number;
}

export interface PayPalStyleOptions {
  layout?: 'horizontal' | 'vertical';
  color?: 'blue' | 'gold' | 'silver' | 'white' | 'black';
  tagline?: boolean;
  branding?: boolean;
  shape?: 'rect' | 'pill';
  label?: 'paypal' | 'checkout' | 'buynow' | 'pay' | 'installment';
}

export interface ConfirmError {
  statusCode: number;
  message: string;
}

export enum PaymentEventName {
  PAYMENT_START = 'payment_start',
  PAYMENT_SUCCESS = 'payment_success',
  PAYMENT_ERROR = 'payment_error',
  PAYMENT_CANCELED = 'payment_cancelled',
}

export interface StartHandlerEvent {
  data: {
    payment: {
      key: typeof Payment.PAYPAL;
    };
  };
}

export interface SuccessHandlerEvent {
  data: {
    payment: {
      key: typeof Payment.PAYPAL;
    };
    redirectUrl: string;
  };
}
export interface CanceledHandlerEvent {
  data: {
    payment: {
      key: typeof Payment.PAYPAL;
    };
  };
}

export interface ErrorHandlerEvent {
  data: {
    payment: {
      key: typeof Payment.PAYPAL;
    };
  } & ConfirmError;
}

export abstract class SDKInstance {
  /** @public mirrors the external checkout SDK contract */
  static PAYMENT_START: PaymentEventName.PAYMENT_START;
  /** @public mirrors the external checkout SDK contract */
  static PAYMENT_SUCCESS: PaymentEventName.PAYMENT_SUCCESS;
  /** @public mirrors the external checkout SDK contract */
  static PAYMENT_ERROR: PaymentEventName.PAYMENT_ERROR;
  /** @public mirrors the external checkout SDK contract */
  static PAYMENT_CANCELED = PaymentEventName.PAYMENT_CANCELED;
  abstract init(options: {
    source: string;
    basketId: string;
    customerId: number | null;
    platformAppId: number;
    baseUrl: string;
  }): void;

  abstract authorize(options: {
    platformAppId: number;
    source: string;
  }): Promise<ExpressCheckoutAuthResponse>;

  abstract loadPayment(options: {
    paymentKey: string;
    configuration: ExpressCheckoutUserData['payment']['configuration'];
    appendRatepayScripts?: boolean;
    htmlContainer: HTMLDivElement | null;
    source: string;
    styleOptions?: PayPalStyleOptions;
  }): void;

  abstract confirm(options: {
    termsAndConditions?: boolean;
    source?: string;
  }): Promise<OrderConfirmation>;

  abstract subscribe(
    eventName: PaymentEventName.PAYMENT_START,
    callback: (event: StartHandlerEvent) => void,
  ): void;

  abstract subscribe(
    eventName: PaymentEventName.PAYMENT_SUCCESS,
    callback: (event: SuccessHandlerEvent) => void,
  ): void;

  abstract subscribe(
    eventName: PaymentEventName.PAYMENT_ERROR,
    callback: (event: ErrorHandlerEvent) => void,
  ): void;

  abstract subscribe(
    eventName: PaymentEventName.PAYMENT_CANCELED,
    callback: (event: CanceledHandlerEvent) => void,
  ): void;

  abstract unsubscribe(
    eventName: PaymentEventName.PAYMENT_START,
    callback: (event: StartHandlerEvent) => void,
  ): void;

  abstract unsubscribe(
    eventName: PaymentEventName.PAYMENT_SUCCESS,
    callback: (event: SuccessHandlerEvent) => void,
  ): void;

  abstract unsubscribe(
    eventName: PaymentEventName.PAYMENT_ERROR,
    callback: (event: ErrorHandlerEvent) => void,
  ): void;

  abstract unsubscribe(
    eventName: PaymentEventName.PAYMENT_CANCELED,
    callback: (event: CanceledHandlerEvent) => void,
  ): void;
}
